import React, { createRef } from "react";
import { isEqual } from "lodash";
import fieldPropTypes from "../field-prop-types";
import { fieldValidation as validation } from "../../utils/field-validation";
import {
  DermTreatmentHeader,
  FIELD_PARAMS,
  FIELD_NAMES
} from "../../utils/constants";
import { idCreator } from "../../utils/id-creator";
import { UNIREG_MASK_KEY } from "../../theme/default/masks";
import ModernizedInlineMessage from "./modernized-inline-message";

const LOCATION_FIELDS = {
  country_cd: true,
  state: true,
  can_state: true
};

class ModernizedTextField extends React.Component {
  constructor(props) {
    super(props);
    const { field, appData, value } = props;
    const { params } = field;

    this.wrapperRef = createRef();
    this.updateControlValidation(field, appData, value);
    this.params = params.split("|").map(p => p.replace("TDFieldOption", ""));
    this.renderTextField = this.renderTextField.bind(this);
    this.updateValueObject = this.updateValueObject.bind(this);
    this.state = {
      hidden: true,
      password: ""
    };
    this.toggleShow = this.toggleShow.bind(this);
    this.handlePasswordChange = this.handlePasswordChange.bind(this);
    this.updateFieldValidation = this.updateFieldValidation.bind(this);
  }

  componentDidMount() {
    const {
      field,
      field: { name, params },
      appData: { values, handleSelectChange, handleChange }
    } = this.props;

    if (
      LOCATION_FIELDS[name] &&
      (values[name] === "" || values[name].value === "")
    ) {
      handleSelectChange({ [name]: "currentLocation" }, field);
    }

    if (this.props.password) {
      this.setState({ password: this.props.password });
    }

    if (params.includes("TDFieldOptionPassword")) {
      handleChange({ [name]: "" });
    }

    this.resetFieldValidation();
  }

  componentDidUpdate() {
    const { field, appData, value } = this.props;
    this.updateControlValidation(field, appData, value);
  }

  handlePasswordChange(e) {
    this.setState({ password: e.target.value });

    if (e.target.value === "") {
      this.setState({ hidden: true });
    }

    this.updateValue(e);
  }

  updateValue = e => {
    e.preventDefault();
    const {
      appData: { values, handleChange, isUnifiedRegistration }
    } = this.props;
    const { value } = e.target;
    const name = e.target.getAttribute("name") || "";

    if (
      value !== values[name] ||
      (isUnifiedRegistration && e.type === "blur")
    ) {
      handleChange({ [name]: value });
      this.updateFieldValidation(e);
    }
  };

  getMaskExtraParam = () => {
    const {
      appData: { isUnifiedRegistration, brandCountryCode },
      field: { params, data }
    } = this.props;
    if (data?.locale) {
      return data.locale;
    }
    if (params.includes("DateOfBirth") && isUnifiedRegistration) {
      return brandCountryCode;
    }
    if (params.includes("PhoneNumber") && isUnifiedRegistration) {
      return UNIREG_MASK_KEY;
    }
  };

  extractConfig = (config, extraParam) =>
    this.params.reduce(
      (configExtended, fieldParam) =>
        config[fieldParam] && config[fieldParam].hasNesting
          ? Object.assign(
              configExtended,
              config[fieldParam][extraParam] || config[fieldParam].default
            )
          : Object.assign(configExtended, config[fieldParam]),
      {}
    );

  toggleShow() {
    this.setState(state => ({
      hidden: !state.hidden
    }));
  }

  updateControlValidation(field, appData) {
    const isFieldVisible =
      !appData.assesmentsState[field.name] ||
      appData.assesmentsState[field.name].visible;

    if (isFieldVisible) {
      validation[field.type].update(field, appData, {
        fieldRef: this.wrapperRef
      });
    }
  }

  updateValueObject(name, value) {
    const {
      appData: { values, resetFieldValidation, handleChange }
    } = this.props;
    if (!isEqual(value, values[name])) {
      resetFieldValidation(name);
      handleChange({ [name]: value });
    }
  }

  preventFormCompletion(event) {
    if (event.keyCode === 13) {
      event.preventDefault();
      return false;
    }
  }

  resetFieldValidation(value, isSelect) {
    const { name } = this.props.field;
    const {
      resetFieldValidation,
      resetFieldValidationAll,
      isPulse,
      isEnhancedRedesign,
      isUnifiedRegistration
    } = this.props.appData;
    resetFieldValidation(name);
    const shouldResetAllErrors =
      isSelect &&
      (isEnhancedRedesign || isPulse || isUnifiedRegistration) &&
      name === FIELD_NAMES.searchBy &&
      value !== this.props.value;
    if (shouldResetAllErrors) {
      resetFieldValidationAll();
    }
  }

  updateFieldValidation(e) {
    const {
      field,
      appData,
      appData: {
        setFieldValidation,
        resetFieldValidation,
        isUnifiedRegistration
      }
    } = this.props;

    if (isUnifiedRegistration && e.type === "blur") {
      const validatedField = validation[field.type].update(field, appData, {
        fieldRef: this.wrapperRef
      });
      setFieldValidation({ [field.name]: validatedField });

      this.updateFormGroupErrors(field.name, validatedField);
    } else {
      resetFieldValidation(field.name);
    }
  }

  isInvalid() {
    const { name } = this.props.field;
    const { fieldValidation } = this.props.appData;
    return fieldValidation[name] && !fieldValidation[name].isValid;
  }

  renderTextField(isInvalid) {
    const {
      components,
      appData: { masks, fieldAttributes: attr, values, isUnifiedRegistration },
      field: {
        name,
        text,
        params,
        placeholder,
        warningMessage,
        disabled,
        readOnly
      },
      value
    } = this.props;
    const { ModernizedInputField } = components;
    const { modernizedLabelId, modernizedInputId } = idCreator(name);
    const maskExtraParam = this.getMaskExtraParam();
    const maskingAttributes = this.extractConfig(masks, maskExtraParam);
    const attributes = this.extractConfig(attr);

    const fieldAttributes = {
      name,
      type: !params.includes("TDFieldOptionPassword")
        ? "text"
        : this.state.hidden
        ? "password"
        : "text",
      value: params.includes("TDFieldOptionPassword")
        ? this.state.password
        : DermTreatmentHeader.includes(name)
        ? values.term
        : value || values[name] || text,
      defaultValue: text,
      ...attributes,
      autoComplete: "off"
    };

    const renderTextField = () => (
      <ModernizedInputField
        name={name}
        id={modernizedInputId}
        labelId={modernizedLabelId}
        fieldAttributes={fieldAttributes}
        maskingAttributes={maskingAttributes}
        placeholder={placeholder}
        updateValue={this.updateValue}
        onEnterDown={this.preventFormCompletion}
        invalid={isInvalid}
        onChange={this.handlePasswordChange}
        disabled={disabled}
        readOnly={readOnly}
        params={params}
        isUnifiedRegistration={isUnifiedRegistration}
        warningMessage={warningMessage}
      />
    );

    return renderTextField(isInvalid);
  }

  renderField(isInvalid) {
    const {
      field: { name },
      appData
    } = this.props;

    const isFieldVisible =
      !appData.assesmentsState[name] || appData.assesmentsState[name].visible;

    if (!isFieldVisible) {
      return null;
    }

    return this.renderTextField(isInvalid);
  }

  render() {
    const {
      components,
      appData,
      field: {
        name,
        label,
        helperText,
        params,
        warningMessage,
        accessibility = {},
        required
      }
    } = this.props;
    const { label: textLabel } = accessibility;
    const { modernizedLabelId, modernizedInputId } = idCreator(name);
    const { ModernizedLabel } = components;
    this.isRequired = required;
    const isWarning = !!warningMessage;
    const isInvalid = this.isInvalid();
    const errors = this.props.appData.fieldValidation?.[name];
    const { errorMessage } = this.props.appData.fieldValidation?.[name] ?? {};
    const visibilityClass = params.includes(FIELD_PARAMS.fieldOptionHidden)
      ? "hidden-field"
      : "";
    const concatErrorMessages =
      isInvalid && !errorMessage && !errors
        ? errors.reduce(
            (error, error_message) => `${error_message + error.errorMessage} `
          )
        : errorMessage;
    const variants = {
      error: "ic_pulse_error_default",
      warning: "ic_pulse_alert_default"
    };
    return (
      <div
        className={`modernized-field-wrapper-web-sdk modernized-text-field-${name} ${visibilityClass}`}
        ref={this.wrapperRef}
      >
        <ModernizedLabel
          name={name}
          id={modernizedLabelId}
          inputId={modernizedInputId}
          text={label}
          isRequired={this.isRequired}
          params={params}
          invalid={isInvalid}
          appData={appData}
          srLabel={textLabel}
          helperText={helperText}
        />
        {this.renderField(isInvalid)}
        {(isInvalid || isWarning) && (
          <ModernizedInlineMessage
            field={{
              image: isInvalid ? variants.error : variants.warning,
              title: isInvalid
                ? !errorMessage
                  ? concatErrorMessages
                  : errorMessage
                : warningMessage
            }}
            components={components}
          >
            {isInvalid && errors && !errorMessage
              ? errors.map(error => error.errorMessage)
              : errorMessage}
          </ModernizedInlineMessage>
        )}
      </div>
    );
  }
}

ModernizedTextField.propTypes = fieldPropTypes;

export default ModernizedTextField;
